/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import { copyWithToast } from "../../lib/clipboard";
import { cn } from "../../lib/utils";

/**
 * A labeled identifier the coordinator can copy with one tap — the bridge from a stream to the
 * record in Healthie. Click copies the value and toasts; the icon flips to a check briefly.
 */
export function CopyableId({
  label,
  value,
  hideLabel = false,
  className,
}: {
  label: string;
  value: string;
  /** Show the value alone, where a row label already says what it is. */
  hideLabel?: boolean;
  className?: string;
}) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  async function copy() {
    const done = await copyWithToast(
      value,
      showToast,
      `${label} ID copied`,
      `Couldn't copy the ${label} ID`,
    );
    if (done) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={`Copy ${label} ID`}
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs transition-colors hover:border-border-hover",
        className,
      )}
    >
      {!hideLabel && <span className="shrink-0 text-text-tertiary">{label}</span>}
      <span className="min-w-0 truncate font-mono text-text-secondary group-hover:text-text-primary">
        {value}
      </span>
      {copied ? (
        <Check size={12} className="shrink-0 text-success" />
      ) : (
        <Copy size={12} className="shrink-0 text-text-tertiary group-hover:text-text-primary" />
      )}
    </button>
  );
}
